import LocalizedTextField from '@components/Customization/LocalizedTextField';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { Box, Button, Divider, Drawer, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
} from '@pages/CustomPoiTypes.styles';
import { hasText, resolveLabel, type LocalizedText, type PoiType } from '@store/api/poiTypeEndpoints';
import { useEffect, useState } from 'react';
import { TimesIcon } from 'react-line-awesome';

export interface PoiTypeDraft {
  name: LocalizedText;
  description: string;
  inheritFrom: string | null;
  teamId: string;
}

interface Props {
  open: boolean;
  /** The type being edited, or null when creating one. */
  poiType: PoiType | null;
  /** Types offered as a parent — already filtered of this type and its descendants. */
  parentCandidates: PoiType[];
  /** Teams the caller may create a type in. Only asked for on create. */
  teams: { id: string; name: string }[];
  saving: boolean;
  onClose: () => void;
  onSave: (draft: PoiTypeDraft) => void;
}

const PoiTypeDrawer: React.FC<Props> = ({
  open, poiType, parentCandidates, teams, saving, onClose, onSave,
}) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  const [draft, setDraft] = useState<PoiTypeDraft>({ name: {}, description: '', inheritFrom: null, teamId: '' });

  useEffect(() => {
    if (!open) return;

    setDraft(poiType
      ? {
        name: { ...poiType.name },
        description: poiType.description ?? '',
        inheritFrom: poiType.inheritFrom,
        teamId: poiType.teamId,
      }
      : { name: {}, description: '', inheritFrom: null, teamId: teams[0]?.id ?? '' });
  }, [open, poiType, teams]);

  const parent = parentCandidates.find((candidate) => candidate._id === draft.inheritFrom);
  const canSave = hasText(draft.name) && Boolean(draft.teamId) && !saving;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 480 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
              {poiType ? t('Customization_EditPoiType') : t('Customization_NewPoiType')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('Customization_PoiTypeSubtitle')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={drawerCloseStyles} disabled={saving}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Stack spacing={2} sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          <LocalizedTextField
            required
            autoFocus
            label={t('Common_Name')}
            value={draft.name}
            onChange={(name) => setDraft((current) => ({ ...current, name }))}
            placeholder={t('Customization_PoiTypeNamePlaceholder')}
            helperText={t('Customization_PoiTypeNameHelp')}
          />

          <TextField
            fullWidth
            size="small"
            label={t('Common_Description')}
            placeholder={t('Customization_PoiTypeDescriptionPlaceholder')}
            value={draft.description}
            onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
            disabled={saving}
          />

          {/* The owning team decides who may edit the type, so it is fixed once created. */}
          {poiType ? null : (
            <TextField
              select
              fullWidth
              size="small"
              label={t('Customization_OwningTeam')}
              value={draft.teamId}
              onChange={(event) => setDraft((current) => ({ ...current, teamId: event.target.value }))}
              helperText={t('Customization_OwningTeamHelp')}
              disabled={saving}
            >
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            fullWidth
            size="small"
            label={t('Customization_InheritFrom')}
            value={draft.inheritFrom ?? ''}
            onChange={(event) => setDraft((current) => ({ ...current, inheritFrom: event.target.value || null }))}
            helperText={parent
              ? t('Customization_InheritFromSelected', [resolveLabel(parent.name, locales)])
              : t('Customization_InheritFromHelp')}
            disabled={saving}
          >
            <MenuItem value="">{t('Customization_InheritNone')}</MenuItem>
            {parentCandidates.map((candidate) => (
              <MenuItem key={candidate._id} value={candidate._id}>
                {resolveLabel(candidate.name, locales)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Divider />

        <Stack direction="row" alignItems="center" spacing={1} sx={drawerFooterStyles}>
          {/* Always rendered, even when it carries no hint, so the buttons keep their
              right-hand position whether the drawer is creating or editing. */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {!poiType && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {t('Customization_AddFieldsAfterCreating')}
              </Typography>
            )}
          </Box>
          <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles} disabled={saving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => onSave(draft)}
            sx={buttonStyles}
            disabled={!canSave}
          >
            {poiType ? t('Common_Save') : t('Customization_CreateType')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default PoiTypeDrawer;
